/**
 * Contact Map Component
 * Lazy-loads Google Maps when the map container enters the viewport.
 * Uses Geocoding to convert address to coordinates.
 */

declare global {
    interface Window {
        google?: typeof google;
    }
}

const DEFAULT_CENTER = { lat: 52.1326, lng: 5.2913 }; // Netherlands center
const DEFAULT_ZOOM = 15;

function loadGoogleMapsScript(apiKey: string): Promise<void> {
    if (window.__googleMapsLoadPromise) {
        return window.__googleMapsLoadPromise;
    }

    window.__googleMapsLoadPromise = new Promise((resolve, reject) => {
        if (typeof google !== 'undefined' && google.maps) {
            resolve();
            return;
        }

        const callbackName = '__contactMapLoadCallback';
        (window as any)[callbackName] = () => resolve();

        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=${callbackName}`;
        script.onerror = () => reject(new Error('Google Maps failed to load'));
        document.head.appendChild(script);
    });

    return window.__googleMapsLoadPromise;
}

declare global {
    interface Window {
        __googleMapsLoadPromise?: Promise<void>;
    }
}

function initMap(container: HTMLElement, address: string): void {
    const map = new google.maps.Map(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        gestureHandling: 'cooperative',
    });

    if (address && address.trim()) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: address.trim() }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                const location = results[0].geometry!.location;
                map.setCenter(location);
                new google.maps.Marker({ map, position: location });
            } else {
                new google.maps.Marker({ map, position: DEFAULT_CENTER });
            }
        });
    } else {
        new google.maps.Marker({ map, position: DEFAULT_CENTER });
    }
}

export class ContactMap {
    private observer: IntersectionObserver | null = null;
    private containers: NodeListOf<HTMLElement> = [] as unknown as NodeListOf<HTMLElement>;

    constructor() {
        this.containers = document.querySelectorAll('[data-contact-map]');
        if (this.containers.length === 0) return;

        this.init();
    }

    private init(): void {
        if (!('IntersectionObserver' in window)) {
            this.containers.forEach((el) => this.loadMap(el));
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const el = entry.target as HTMLElement;
                        this.loadMap(el);
                        this.observer?.unobserve(el);
                    }
                });
            },
            { rootMargin: '100px', threshold: 0.1 }
        );

        this.containers.forEach((el) => this.observer?.observe(el));
    }

    private async loadMap(container: HTMLElement): Promise<void> {
        const apiKey = container.dataset.apiKey;
        const address = container.dataset.address ?? '';

        if (!apiKey) {
            container.innerHTML = '<p class="text-gray-600 p-8 text-center">Map configuration missing</p>';
            container.classList.remove('flex', 'items-center', 'justify-center');
            return;
        }

        try {
            await loadGoogleMapsScript(apiKey);
            initMap(container, address);
            container.classList.remove('flex', 'items-center', 'justify-center');
            container.querySelector('.contact-map-placeholder')?.remove();
        } catch (err) {
            console.error('Contact map failed to load:', err);
            container.innerHTML =
                '<p class="text-gray-600 p-8 text-center">Map could not be loaded</p>';
            container.classList.remove('flex', 'items-center', 'justify-center');
        }
    }

    public destroy(): void {
        this.observer?.disconnect();
        this.observer = null;
    }
}

export function initContactMap(): ContactMap {
    return new ContactMap();
}
